/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   roles.ts
 *     Description     :   Spec 285 — the canonical platform role vocabulary, shared by both
 *                         planes. Viewer < Manager < Operator < Admin < SuperAdmin (FCP
 *                         ADR 0091). SuperAdmin is the platform-administration role and a
 *                         CONTEXT SELECTOR, never a cross-tenant filter bypass. This module
 *                         is additive: it does not rewrite the legacy UserRole/UserRoleValue
 *                         unions (bounded consolidation is a tracked follow-up), it gives the
 *                         access decision one strict vocabulary to reason over.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */

/** The canonical, PascalCase platform role set. */
export type Role = 'Viewer' | 'Manager' | 'Operator' | 'Admin' | 'SuperAdmin';

/** Authority rank (for convenience/derivation only — nav access uses explicit role SETS). */
export const ROLE_RANK: Record<Role, number> = {
  Viewer: 1,
  Manager: 2,
  Operator: 3,
  Admin: 4,
  SuperAdmin: 5,
};

const CANONICAL: Role[] = ['Viewer', 'Manager', 'Operator', 'Admin', 'SuperAdmin'];

/** True only for the platform-administration role. */
export function isSuperAdmin(role: Role): boolean {
  return role === 'SuperAdmin';
}

/**
 * Strict, case-insensitive parse of an arbitrary role value to the canonical set.
 * Returns `null` for anything unrecognised — the caller MUST treat null as DENY
 * (fail-closed, FR-021). Does NOT fold legacy values; use normalizeDbRole for that.
 */
export function toRole(raw: string | null | undefined): Role | null {
  if (!raw) return null;
  const lower = String(raw).trim().toLowerCase();
  if (!lower) return null;
  return CANONICAL.find((r) => r.toLowerCase() === lower) ?? null;
}

/**
 * The one check every legacy role comparison uses (Spec 285 follow-up): true when the role is in
 * `allowed`, or is SuperAdmin and `allowed` contains Admin — SuperAdmin ⊇ Admin, so any check an
 * Admin passes, a SuperAdmin passes too. Exact sets stay exact: SuperAdmin is NOT widened into a
 * set that excludes Admin. Case-insensitive; empty/unknown fails closed.
 *
 * Why it exists: making SuperAdmin a live role left ~a dozen checks comparing against 'Admin' by
 * name, which treated the strongest role worse than Admin (2026-10-01, found on prod). The guard
 * __tests__/superAdminRoleParity.guard.test.ts keeps new ones out.
 */
export function roleSatisfies(raw: string | null | undefined, allowed: readonly Role[]): boolean {
  const role = toRole(raw);
  if (!role) return false;
  if (allowed.includes(role)) return true;
  return role === 'SuperAdmin' && allowed.includes('Admin');
}

/**
 * Normalise a value stored in dbo.users.role to a canonical Role, folding the legacy
 * `User` value to `Viewer` (Spec 179/190) and preserving `SuperAdmin`. Unknown/empty
 * defaults to `Viewer` (the lowest authority) — distinct from toRole, which is strict.
 */
export function normalizeDbRole(raw: string | null | undefined): Role {
  const lower = String(raw ?? '').trim().toLowerCase();
  if (lower === 'user') return 'Viewer';
  return toRole(lower) ?? 'Viewer';
}
